import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { AppThemeDoc, ThemeAssignments, ThemeableComponent } from '@shared/types';
import { subscribeToExerciseThemes } from '@shared/services/exerciseThemeService';
import { extractClassValues, generateDynamicCss } from '@shared/utils/dynamicThemeStyles';
import { getCompleteTheme } from '@shared/utils/themeDefaults';
import {
  ThemeConfig,
  exerciseThemes as defaultThemes,
  completedTheme as defaultCompletedTheme,
  getTheme as defaultGetTheme,
} from '../themes/defaultExerciseThemes';
import {
  DEFAULT_ACTION_BUTTONS,
  DEFAULT_STATUS_BADGES,
  DEFAULT_PROGRESS_BAR,
  DEFAULT_CHART_COLORS,
  DEFAULT_ICON_ACCENTS,
  DEFAULT_STATE_STYLES,
} from '@shared/utils/themeDefaults';

/** A complete default theme with all extension fields for app-wide use */
const FALLBACK_APP_THEME: ThemeConfig = {
  ...defaultThemes.sage,
  actionButtons: DEFAULT_ACTION_BUTTONS,
  statusBadges: DEFAULT_STATUS_BADGES,
  progressBar: DEFAULT_PROGRESS_BAR,
  chartColors: DEFAULT_CHART_COLORS,
  iconAccents: DEFAULT_ICON_ACCENTS,
  stateStyles: DEFAULT_STATE_STYLES,
};

interface ExerciseThemeContextValue {
  getTheme: (name: string) => ThemeConfig;
  /** Get the assigned theme for a component type (transactions, goals, charts, focusItems) */
  getComponentTheme: (component: ThemeableComponent) => ThemeConfig;
  completedTheme: ThemeConfig;
  /** The default app theme (isDefault=true from Firestore, or fallback) */
  defaultTheme: ThemeConfig;
  themes: AppThemeDoc[];
  assignments: ThemeAssignments;
  isReady: boolean;
}

const ExerciseThemeContext = createContext<ExerciseThemeContextValue>({
  getTheme: defaultGetTheme,
  getComponentTheme: () => FALLBACK_APP_THEME,
  completedTheme: defaultCompletedTheme,
  defaultTheme: FALLBACK_APP_THEME,
  themes: [],
  assignments: {},
  isReady: false,
});

/**
 * Convert a Firestore AppThemeDoc to the ThemeConfig shape used by components.
 * Applies defaults for optional extension fields.
 */
const docToThemeConfig = (doc: AppThemeDoc): ThemeConfig => {
  const complete = getCompleteTheme(doc);
  return {
    name: doc.name,
    colors: { ...doc.colors },
    card: { ...doc.card },
    button: { ...doc.button },
    decorative: { ...doc.decorative },
    actionButtons: complete.actionButtons,
    statusBadges: complete.statusBadges,
    progressBar: complete.progressBar,
    chartColors: complete.chartColors,
    iconAccents: complete.iconAccents,
    stateStyles: complete.stateStyles,
  };
};

const TIMEOUT_MS = 2000;

export const ExerciseThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firestoreThemes, setFirestoreThemes] = useState<AppThemeDoc[]>([]);
  const [isReady, setIsReady] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const receivedDataRef = useRef(false);

  // Keep assignments as empty object for backward compat
  const assignments: ThemeAssignments = {};

  useEffect(() => {
    // Start timeout — if Firestore hasn't responded in TIMEOUT_MS, mark ready anyway
    timeoutRef.current = setTimeout(() => {
      if (!receivedDataRef.current) {
        setIsReady(true);
      }
    }, TIMEOUT_MS);

    const unsubscribeThemes = subscribeToExerciseThemes((themes) => {
      receivedDataRef.current = true;
      setFirestoreThemes(themes);
      setIsReady(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    });

    return () => {
      unsubscribeThemes();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Build a lookup map from Firestore themes
  const firestoreMap = useMemo(() => {
    const map: Record<string, ThemeConfig> = {};
    for (const t of firestoreThemes) {
      if (t.active) {
        map[t.id] = docToThemeConfig(t);
      }
    }
    return map;
  }, [firestoreThemes]);

  // Inject dynamic CSS for any arbitrary hex colors in Firestore themes
  useEffect(() => {
    const allValues = firestoreThemes
      .filter((t) => t.active)
      .flatMap((t) => {
        const fields: Record<string, any> = {
          colors: t.colors,
          card: t.card,
          button: t.button,
          decorative: t.decorative,
        };
        if (t.actionButtons) fields.actionButtons = t.actionButtons;
        if (t.statusBadges) fields.statusBadges = t.statusBadges;
        if (t.progressBar) fields.progressBar = t.progressBar;
        if (t.chartColors) fields.chartColors = t.chartColors;
        if (t.stateStyles) fields.stateStyles = t.stateStyles;
        return extractClassValues(fields);
      });



    const css = generateDynamicCss(allValues);
    if (!css) {
      // Remove any previously injected style element
      document.getElementById('dynamic-theme-styles')?.remove();
      return;
    }

    let styleEl = document.getElementById('dynamic-theme-styles') as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dynamic-theme-styles';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;

 

    return () => {
      document.getElementById('dynamic-theme-styles')?.remove();
    };
  }, [firestoreThemes]);

  const completedTheme = useMemo(() => {
    const completedDoc = firestoreThemes.find(t => t.isCompleted && t.active);
    return completedDoc ? docToThemeConfig(completedDoc) : defaultCompletedTheme;
  
  }, [firestoreThemes]);

  /** The default app-wide theme (isDefault=true from Firestore, or fallback) */
  const defaultTheme = useMemo(() => {
    const defaultDoc = firestoreThemes.find(t => t.isDefault && t.active);
    return defaultDoc ? docToThemeConfig(defaultDoc) : FALLBACK_APP_THEME;
  }, [firestoreThemes]);

  const getTheme = useMemo(() => {
    return (name: string): ThemeConfig => {
      // Firestore first, then hardcoded default
      if (firestoreMap[name]) return firestoreMap[name];
      return defaultGetTheme(name);
    };

  }, [firestoreMap]);

  /** Get the default theme for a component type by filtering themes with matching componentType */
  const getComponentTheme = useMemo(() => {
    return (component: ThemeableComponent): ThemeConfig => {
      // Find the default theme scoped to this component type
      const scopedDefault = firestoreThemes.find(
        t => t.active && t.componentType === component && t.isDefault
      );
      if (scopedDefault) {
        return docToThemeConfig(scopedDefault);
      }
      // Fall back to any active theme for this component type
      const anyScoped = firestoreThemes.find(
        t => t.active && t.componentType === component
      );
      if (anyScoped) {
        return docToThemeConfig(anyScoped);
      }
      // Fall back to the global default theme
      return defaultTheme;
    };
  }, [firestoreThemes, defaultTheme]);

  const value = useMemo(
    () => ({ getTheme, getComponentTheme, completedTheme, defaultTheme, themes: firestoreThemes, assignments, isReady }),
    [getTheme, getComponentTheme, completedTheme, defaultTheme, firestoreThemes, assignments, isReady]
  );

  return (
    <ExerciseThemeContext.Provider value={value}>
      {children}
    </ExerciseThemeContext.Provider>
  );
};

export const useExerciseThemes = () => useContext(ExerciseThemeContext);

/** Alias for useExerciseThemes - same hook, clearer name for app-wide theming */
export const useAppThemes = useExerciseThemes;
